import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Layout } from "./components/layout/Layout";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { LeadsPage } from "./pages/LeadsPage";
import { LeadDetailPage } from "./pages/LeadDetailPage";
import { KanbanPage } from "./pages/KanbanPage";
import { ImportPage } from "./pages/ImportPage";
import { CampaignsPage } from "./pages/CampaignsPage";
import { SourcesPage } from "./pages/SourcesPage";
import { SellersPage } from "./pages/SellersPage";
import { SettingsPage } from "./pages/SettingsPage";
import { AuditPage } from "./pages/AuditPage";
import { DuplicatesPage } from "./pages/DuplicatesPage";
import { EmailSettingsPage } from "./pages/EmailSettingsPage";
import { LeadFlowPage } from "./pages/LeadFlowPage";
import { Loader2 } from "lucide-react";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
};

// App Routes
const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/leads"
        element={
          <ProtectedRoute>
            <LeadsPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/leads/:id"
        element={
          <ProtectedRoute>
            <LeadDetailPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/kanban"
        element={
          <ProtectedRoute>
            <KanbanPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/import"
        element={
          <ProtectedRoute adminOnly>
            <ImportPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/campaigns"
        element={
          <ProtectedRoute adminOnly>
            <CampaignsPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/sources"
        element={
          <ProtectedRoute adminOnly>
            <SourcesPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/sellers"
        element={
          <ProtectedRoute adminOnly>
            <SellersPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/settings"
        element={
          <ProtectedRoute adminOnly>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/audit"
        element={
          <ProtectedRoute adminOnly>
            <AuditPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/duplicates"
        element={
          <ProtectedRoute adminOnly>
            <DuplicatesPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/email-settings"
        element={
          <ProtectedRoute>
            <EmailSettingsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/leadflow"
        element={
          <ProtectedRoute adminOnly>
            <LeadFlowPage />
          </ProtectedRoute>
        }
      />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
